import type { LanguageController, LanguageState } from "../../i18n/languageController";
import type { LocaleCode } from "../../i18n/locales";

type Controller = Pick<LanguageController, "getSnapshot" | "subscribe" | "apply">;
type Selection = { generation: number; mode: LanguageState["mode"]; locale: LocaleCode | null; complete: boolean };

/** A completion belongs to one mounted menu and one identity generation. */
export function createLanguageMenuSelection(controller: Controller, close: () => void) {
  let mounted = false;
  let pending: Selection | null = null;
  const check = () => {
    const state = controller.getSnapshot();
    if (!pending) return;
    if (pending.generation !== state.generation || pending.mode !== state.mode) { pending = null; return; }
    const confirmed = state.mode === "guest" ? state.guestLocale : state.sessionLocale;
    if (mounted && pending.complete && state.saveStatus === "saved" && state.contextStatus === "ready"
      && state.catalogStatus === "ready" && state.displayedLocale === state.effectiveLocale
      && confirmed === pending.locale && (state.mode !== "guest" || state.storageAvailable)) {
      pending = null;
      close();
    }
  };
  return {
    mount() {
      mounted = true;
      const unsubscribe = controller.subscribe(check);
      return () => { mounted = false; pending = null; unsubscribe(); };
    },
    async select(locale: LocaleCode | null) {
      check();
      const state = controller.getSnapshot();
      if (!mounted || state.contextStatus !== "ready" || state.saveStatus === "saving" || state.saveStatus === "uncertain"
        || (pending && !pending.complete)) return;
      const ticket: Selection = { generation: state.generation, mode: state.mode, locale, complete: false };
      pending = ticket;
      try { await controller.apply({ scope: state.mode === "guest" ? "guest" : "session", locale }); }
      finally { if (pending === ticket) { ticket.complete = true; check(); } }
    },
  };
}
